// pages/menus/MenuListPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Search, Loader, AlertCircle, ChevronLeft, ChevronRight, User, MessageCircle, X, Phone } from 'lucide-react';

/** Filled WhatsApp logo (green when number assigned) */
const WhatsAppIconFilled = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.865 9.865 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
import { menuService } from '../../services/menuService';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from 'react-i18next';

// Updated interface to match API response
interface Menu {
  id: number;
  title: string;
  description: string;
  status: string;
  theme: string;
  backgroundColor: string;
  primaryColor: string;
  slug?: string;
  publicMenuUrl?: string;
  createdAt: string;
  updatedAt: string;
  hasWhatsAppNumber?: boolean;
  statistics: {
    categories: number;
    dishes: number;
    views: number;
    qrScans: number;
  };
  restaurant: {
    id: number;
    name: string;
    address: string;
    phone: string;
    website?: string;
    logoUrl: string | null;
  } | null;
  createdBy?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalMenus: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
}

const MenuListPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [allMenus, setAllMenus] = useState<Menu[]>([]); // Store all loaded menus
  const [filteredMenus, setFilteredMenus] = useState<Menu[]>([]); // Store filtered results
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    totalMenus: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });

  const { user } = useAuthStore();
  const isAdminOrAgency = user?.role === 'admin' || user?.role === 'agency';
  const [whatsappPopup, setWhatsappPopup] = useState<{ menuId: number; numbers: any[]; loading: boolean } | null>(null);

  const handleShowWhatsApp = async (menuId: number) => {
    // Toggle off if same menu clicked again
    if (whatsappPopup && whatsappPopup.menuId === menuId) {
      setWhatsappPopup(null);
      return;
    }
    setWhatsappPopup({ menuId, numbers: [], loading: true });
    try {
      const res = await menuService.getMenuWhatsAppNumbers(menuId.toString());
      if (res.success) {
        setWhatsappPopup({ menuId, numbers: res.data || [], loading: false });
      } else {
        setWhatsappPopup({ menuId, numbers: [], loading: false });
      }
    } catch {
      setWhatsappPopup({ menuId, numbers: [], loading: false });
    }
  };

  // Close WhatsApp popup when clicking outside
  useEffect(() => {
    if (!whatsappPopup) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-whatsapp-popup]')) {
        setWhatsappPopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [whatsappPopup]);

  // Debounce search term for client-side filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Don't reset page for client-side search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch menus
  const fetchMenus = async () => {
    try {
      setLoading(true);
      setError(null);
        
      const response = await menuService.getAllMenus({
        page: pagination.currentPage,
        limit: pagination.limit,
        search: debouncedSearchTerm || undefined
      });
        
      if (response.success) {
        // Handle both response formats (with pagination or direct array)
        let menusData: any[] = [];
        let paginationData = pagination;
  
        if (response.data && Array.isArray(response.data)) {
          // Backend returned array directly (old format)
          menusData = response.data as any[];
          paginationData = {
            currentPage: 1,
            totalPages: 1,
            totalMenus: menusData.length,
            hasNextPage: false,
            hasPrevPage: false,
            limit: pagination.limit
          };
        } else if (response.data && response.data.menus) {
          // Backend returned proper structure with pagination
          menusData = (response.data.menus as any[]) || [];
          paginationData = response.data.pagination || paginationData;
        } else {
          // Fallback
          menusData = [];
        }
  
        // Transform the API response to match our interface
        const transformedMenus = menusData.map((menu: any) => ({
          ...menu,
          status: menu.status || (menu.isActive ? 'active' : 'inactive'),
          statistics: menu.statistics || {
            categories: 0,
            dishes: 0,
            views: 0,
            qrScans: 0
          }
        }));
  
        // For client-side search, we want to load more data initially
        // or implement a strategy to load all data for searching
        setAllMenus(transformedMenus);
        setFilteredMenus(transformedMenus); // Initially show all menus
        setPagination(paginationData);
      } else {
        setError(response.error || t('listing_menu.errors.fetch_error'));
        toast.error(response.error || t('listing_menu.errors.fetch_error'));
      }
    } catch (err: any) {
      console.error('Failed to fetch menus:', err);
      setError(t('listing_menu.errors.network_error'));
      toast.error(t('listing_menu.errors.fetch_error'));
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering when search term changes
  useEffect(() => {
    if (debouncedSearchTerm === '') {
      // Show all menus when search is cleared
      setFilteredMenus(allMenus);
    } else {
      // Filter menus based on search term
      const filtered = allMenus.filter(menu => 
        menu.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (menu.restaurant?.name && menu.restaurant.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (menu.description && menu.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
      );
      setFilteredMenus(filtered);
    }
  }, [debouncedSearchTerm, allMenus]);

  // Fetch menus when page changes (but not on search)
  useEffect(() => {
    fetchMenus();
  }, [pagination.currentPage]);

  const handleDeleteMenu = async (menuId: number) => {
    if (!window.confirm(t('listing_menu.errors.delete_confirm'))) {
      return;
    }

    try {
      const response = await menuService.deleteMenu(menuId.toString());
      
      if (response.success) {
        toast.success(t('listing_menu.errors.delete_success'));
        // Refresh menu list
        await fetchMenus();
      } else {
        toast.error(response.error || t('listing_menu.errors.delete_error'));
      }
    } catch (err: any) {
      console.error('Failed to delete menu:', err);
      toast.error(t('listing_menu.errors.delete_error'));
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleRefresh = () => {
    fetchMenus();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleStatusToggle = async (menuId: number, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? false : true;
      const response = await menuService.updateMenu(menuId.toString(), {
        isActive: newStatus
      });

      if (response.success) {
        toast.success(t('listing_menu.errors.status_update_success', { status: newStatus ? t('listing_menu.status.activate').toLowerCase() : t('listing_menu.status.deactivate').toLowerCase() }));
        await fetchMenus();
      } else {
        toast.error(response.error || t('listing_menu.errors.status_update_error'));
      }
    } catch (err: any) {
      console.error('Failed to update menu status:', err);
      toast.error('Failed to update menu status');
    }
  };

  // Helper function to get logo URL
  const getLogoUrl = (menu: Menu) => {
    return menu.restaurant?.logoUrl || null;
  };

  // Build public menu URL based on the selected theme and slug
  const getPublicMenuUrl = (menu: Menu) => {
    const baseUrl = window.location.origin;
    const slug = menu.slug;
    if (!slug) return `${baseUrl}/menu/preview/${menu.id}`;
    const theme = menu.publicMenuUrl || 'default';
    switch (theme) {
      case 'dark': return `${baseUrl}/menu-dark/${slug}`;
      case 'light': return `${baseUrl}/menu-light/${slug}`;
      case 'lightComplex': return `${baseUrl}/menu-lightComplex/${slug}`;
      case 'darkComplex': return `${baseUrl}/menu-darkComplex/${slug}`;
      default: return `${baseUrl}/menu/${slug}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0C0B]">
            {isAdminOrAgency ? t('listing_menu.title.admin') : t('listing_menu.title.user')}
          </h1>
          <p className="text-[#687d76] mt-2">
            {isAdminOrAgency ? t('listing_menu.subtitle.admin') : t('listing_menu.subtitle.user')}
          </p>
        
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-[#0A0C0B] transition-colors"
            disabled={loading}
          >
            {t('listing_menu.buttons.refresh')}
          </button>
          <Link
            to="/menus/create"
            className="inline-flex items-center gap-2 bg-[#0A0C0B] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#1A1C1B] transition-colors"
          >
            <Plus className="h-5 w-5" />
            {t('listing_menu.buttons.create_new_menu')}
          </Link>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-medium text-[#687d76]">
            {t('listing_menu.stats.total_menus')}
          </p>
          <p className="mt-1 text-2xl font-semibold text-[#0A0C0B]">
            {pagination.totalMenus}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-medium text-[#687d76]">
            {t('listing_menu.stats.active_menus')}
          </p>
          <p className="mt-1 text-2xl font-semibold text-[#5a9e8a]">
            {filteredMenus.filter((m: Menu) => m.status === 'active').length}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-medium text-[#687d76]">
            {t('listing_menu.stats.total_categories')}
          </p>
          <p className="mt-1 text-2xl font-semibold text-[#0A0C0B]">
            {filteredMenus.reduce((sum: number, menu: Menu) => sum + (menu.statistics?.categories || 0), 0)}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-medium text-[#687d76]">
            {t('listing_menu.stats.total_dishes')}
          </p>
          <p className="mt-1 text-2xl font-semibold text-[#0A0C0B]">
            {filteredMenus.reduce((sum: number, menu: Menu) => sum + (menu.statistics?.dishes || 0), 0)}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#687d76]" />
          <input
            type="text"
            placeholder={isAdminOrAgency ? t('listing_menu.search.placeholder_admin') : t('listing_menu.search.placeholder_user')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
            disabled={loading}
          />
        </div>
 <div className="flex items-center gap-2">
          <select
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
            onChange={(e) => {
              const filterValue = e.target.value;
              if (filterValue === 'all') {
                // Show all menus
                setFilteredMenus(allMenus);
              } else {
                // Filter by status
                const filtered = allMenus.filter(menu => menu.status === filterValue);
                setFilteredMenus(filtered);
              }
            }}
            disabled={loading}
          >
            <option value="all">{t('listing_menu.filters.all_status')}</option>
            <option value="active">{t('listing_menu.filters.active_only')}</option>
            <option value="inactive">{t('listing_menu.filters.inactive_only')}</option>
          </select>
        </div>      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <div>
              <p className="font-medium text-red-800">{t('listing_menu.errors.loading_error')}</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={handleRefresh}
              className="ml-auto text-sm font-medium text-red-700 hover:text-red-800"
            >
              {t('listing_menu.buttons.try_again')}
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-12">
          <div className="flex flex-col items-center justify-center">
            <Loader className="h-8 w-8 text-[#7BD5B5] animate-spin mb-4" />
            <p className="text-[#687d76]">{t('listing_menu.errors.loading_message')}</p>
          </div>
        </div>
      )}

      {/* Menus Table - Desktop view */}
      {!loading && !error && (
        <>
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50">
                  <tr>
                    {/* {isAdminOrAgency && (
                      <th className="text-left py-2 px-3 sm:py-3 sm:px-6 font-semibold text-[#0A0C0B] text-xs sm:text-sm">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">{t('listing_menu.table.created_by')}</span>
                          <span className="sm:hidden">{t('listing_menu.table.creator')}</span>
                        </div>
                      </th>
                    )} */}
                    <th className="text-left py-2 px-3 sm:py-3 sm:px-6 font-semibold text-[#0A0C0B] text-xs sm:text-sm">
                      <span className="hidden sm:inline">{t('listing_menu.table.menu_name')}</span>
                      <span className="sm:hidden">{t('listing_menu.table.menu')}</span>
                    </th>
                    <th className="text-left py-2 px-3 sm:py-3 sm:px-6 font-semibold text-[#0A0C0B] text-xs sm:text-sm">
                      {t('listing_menu.table.status')}
                    </th>
                    <th className="text-left py-2 px-3 sm:py-3 sm:px-6 font-semibold text-[#0A0C0B] text-xs sm:text-sm">
                      <span className="hidden sm:inline">{t('listing_menu.table.categories')}</span>
                      <span className="sm:hidden">{t('listing_menu.table.cats')}</span>
                    </th>
                    <th className="text-left py-2 px-3 sm:py-3 sm:px-6 font-semibold text-[#0A0C0B] text-xs sm:text-sm">
                      <span className="hidden sm:inline">{t('listing_menu.table.dishes')}</span>
                      <span className="sm:hidden">{t('listing_menu.table.items')}</span>
                    </th>
                    <th className="text-left py-2 px-3 sm:py-3 sm:px-6 font-semibold text-[#0A0C0B] text-xs sm:text-sm">
                      <span className="hidden sm:inline">{t('listing_menu.table.last_updated')}</span>
                      <span className="sm:hidden">{t('listing_menu.table.updated')}</span>
                    </th>
                    <th className="text-left py-2 px-3 sm:py-3 sm:px-6 font-semibold text-[#0A0C0B] text-xs sm:text-sm">
                      {t('listing_menu.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMenus.map((menu) => (
                    <tr key={menu.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                      {/* {isAdminOrAgency && (
                        <td className="py-2 px-3 sm:py-4 sm:px-6">
                          {menu.createdBy ? (
                            <div>
                              <p className="font-medium text-[#0A0C0B] text-xs sm:text-sm">
                                {menu.createdBy.name}
                              </p>
                              <p className="text-xs text-[#687d76] hidden sm:block">
                                {menu.createdBy.email}
                              </p>
                              <span className="inline-block mt-1 px-1.5 py-0.5 sm:px-2 sm:py-0.5 text-xs bg-gray-100 text-gray-800 rounded">
                                {menu.createdBy.role}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-xs">N/A</span>
                          )}
                        </td>
                      )} */}
                      <td className="py-2 px-3 sm:py-4 sm:px-6">
                        <div>
                          <Link 
                            to={`/menus/${menu.id}`}
                            className="font-medium text-[#0A0C0B] hover:text-[#7BD5B5] transition-colors text-xs sm:text-sm"
                          >
                            {menu.title}
                          </Link>
                          <p className="text-xs text-[#687d76] mt-1 line-clamp-1 hidden sm:block">
                            {menu.restaurant?.name || menu.restaurant?.address || 'No restaurant'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1 sm:hidden">
                            {formatDate(menu.createdAt)}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 px-3 sm:py-4 sm:px-6">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs font-medium ${
                            menu.status === 'active'
                              ? 'bg-[#7BD5B5]/10 text-[#5a9e8a]'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {menu.status === 'active' ? t('listing_menu.status.active') : t('listing_menu.status.inactive')}
                          </span>
                          <button
                            onClick={() => handleStatusToggle(menu.id, menu.status)}
                            className="text-xs text-[#687d76] hover:text-[#0A0C0B] hidden sm:block"
                            title={menu.status === 'active' ? t('listing_menu.status.deactivate') : t('listing_menu.status.activate')}
                          >
                            {menu.status === 'active' ? t('listing_menu.status.deactivate') : t('listing_menu.status.activate')}
                          </button>
                        </div>
                      </td>
                      <td className="py-2 px-3 sm:py-4 sm:px-6 text-[#687d76] text-xs sm:text-sm">
                        <div className="flex items-center gap-1">
                          <span>{menu.statistics?.categories || 0}</span>
                          {menu.statistics?.categories === 0 && (
                            <span className="text-xs text-gray-400 hidden sm:inline">({t('listing_menu.table.no_categories')})</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 sm:py-4 sm:px-6 text-[#687d76] text-xs sm:text-sm">
                        <div className="flex items-center gap-1">
                          <span>{menu.statistics?.dishes || 0}</span>
                          {menu.statistics?.dishes === 0 && (
                            <span className="text-xs text-gray-400 hidden sm:inline">({t('listing_menu.table.no_dishes')})</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 sm:py-4 sm:px-6 text-[#687d76] text-xs sm:text-sm">
                        <span className="hidden sm:inline">{formatDate(menu.updatedAt)}</span>
                        <span className="sm:hidden">{formatDate(menu.updatedAt).split(',')[0]}</span>
                      </td>
                      <td className="py-2 px-3 sm:py-4 sm:px-6">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Link
                            to={`/menus/${menu.id}`}
                            className="p-1.5 sm:p-2 text-[#687d76] hover:text-[#0A0C0B] hover:bg-gray-100 rounded-lg transition-colors"
                            title={t('listing_menu.actions.edit_menu')}
                          >
                            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Link>
                          <a
                            href={getPublicMenuUrl(menu)}
                            className="p-1.5 sm:p-2 text-[#687d76] hover:text-[#0A0C0B] hover:bg-gray-100 rounded-lg transition-colors"
                            title={t('listing_menu.actions.view_public_menu')}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                          </a>
                          <div className="relative" data-whatsapp-popup>
                            <button
                              className={`p-1.5 sm:p-2 rounded-lg transition-colors ${menu.hasWhatsAppNumber ? 'text-[#25D366] hover:bg-green-50' : 'text-[#687d76] hover:text-green-600 hover:bg-green-50'}`}
                              title="WhatsApp"
                              onClick={() => handleShowWhatsApp(menu.id)}
                            >
                              {menu.hasWhatsAppNumber ? (
                                <WhatsAppIconFilled className="h-3 w-3 sm:h-4 sm:w-4" />
                              ) : (
                                <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                              )}
                            </button>
                            {whatsappPopup && whatsappPopup.menuId === menu.id && (
                              <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1.5 px-3 whitespace-nowrap">
                                {whatsappPopup.loading ? (
                                  <div className="flex items-center gap-1.5">
                                    <Loader className="h-3 w-3 animate-spin text-green-500" />
                                    <span className="text-xs text-[#687d76]">...</span>
                                  </div>
                                ) : whatsappPopup.numbers.length === 0 ? (
                                  <span className="text-xs text-[#687d76]">{t('listing_menu.whatsapp.no_numbers', 'Nessun numero associato')}</span>
                                ) : (
                                  <div className="flex flex-col gap-1">
                                    {whatsappPopup.numbers.map((num: any, idx: number) => (
                                      <span key={num.id || idx} className="flex items-center gap-1.5 text-xs font-medium text-[#0A0C0B]">
                                        <Phone className="h-3 w-3 text-green-600 flex-shrink-0" />
                                        {num.fullNumber || (num.countryCode ? `${num.countryCode} ${num.phoneNumber}` : num.phoneNumber)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {isAdminOrAgency || user?.id === menu.createdBy?.id ? (
                            <button
                              className="p-1.5 sm:p-2 text-[#687d76] hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title={t('listing_menu.actions.delete_menu')}
                              onClick={() => handleDeleteMenu(menu.id)}
                            >
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </button>
                          ) : (
                            <span 
                              className="p-1.5 sm:p-2 text-gray-400 cursor-not-allowed"
                              title={t('listing_menu.actions.cannot_delete')}
                            >
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Menus Cards - Mobile view */}
          <div className="lg:hidden grid grid-cols-1 gap-4">
            {filteredMenus.map((menu) => (
              <div key={menu.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <Link 
                      to={`/menus/${menu.id}`}
                      className="font-medium text-[#0A0C0B] hover:text-[#7BD5B5] transition-colors text-base block truncate"
                    >
                      {menu.title}
                    </Link>
                    <p className="text-sm text-[#687d76] mt-1 truncate">
                      {menu.restaurant?.name || menu.restaurant?.address || 'No restaurant'}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        menu.status === 'active'
                          ? 'bg-[#7BD5B5]/10 text-[#5a9e8a]'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {menu.status === 'active' ? t('listing_menu.status.active') : t('listing_menu.status.inactive')}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#687d76]">
                        {menu.statistics?.categories || 0} cats
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-[#687d76]">
                        {menu.statistics?.dishes || 0} items
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <div className="text-xs text-[#687d76]">
                    Updated {formatDate(menu.updatedAt)}
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to={`/menus/${menu.id}`}
                      className="p-2 text-[#687d76] hover:text-[#0A0C0B] hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('listing_menu.actions.edit_menu')}
                    >
                      <Edit className="h-4 w-4" />
                    </Link>
                    <a
                      href={getPublicMenuUrl(menu)}
                      className="p-2 text-[#687d76] hover:text-[#0A0C0B] hover:bg-gray-100 rounded-lg transition-colors"
                      title={t('listing_menu.actions.view_public_menu')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                    <div className="relative" data-whatsapp-popup>
                      <button
                        className={`p-2 rounded-lg transition-colors ${menu.hasWhatsAppNumber ? 'text-[#25D366] hover:bg-green-50' : 'text-[#687d76] hover:text-green-600 hover:bg-green-50'}`}
                        title="WhatsApp"
                        onClick={() => handleShowWhatsApp(menu.id)}
                      >
                        {menu.hasWhatsAppNumber ? (
                          <WhatsAppIconFilled className="h-4 w-4" />
                        ) : (
                          <MessageCircle className="h-4 w-4" />
                        )}
                      </button>
                      {whatsappPopup && whatsappPopup.menuId === menu.id && (
                        <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1.5 px-3 whitespace-nowrap">
                          {whatsappPopup.loading ? (
                            <div className="flex items-center gap-1.5">
                              <Loader className="h-3 w-3 animate-spin text-green-500" />
                              <span className="text-xs text-[#687d76]">...</span>
                            </div>
                          ) : whatsappPopup.numbers.length === 0 ? (
                            <span className="text-xs text-[#687d76]">{t('listing_menu.whatsapp.no_numbers', 'Nessun numero associato')}</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {whatsappPopup.numbers.map((num: any, idx: number) => (
                                <span key={num.id || idx} className="flex items-center gap-1.5 text-xs font-medium text-[#0A0C0B]">
                                  <Phone className="h-3 w-3 text-green-600 flex-shrink-0" />
                                  {num.fullNumber || (num.countryCode ? `${num.countryCode} ${num.phoneNumber}` : num.phoneNumber)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isAdminOrAgency || user?.id === menu.createdBy?.id ? (
                      <button
                        className="p-2 text-[#687d76] hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title={t('listing_menu.actions.delete_menu')}
                        onClick={() => handleDeleteMenu(menu.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : (
                      <span 
                        className="p-2 text-gray-400 cursor-not-allowed"
                        title={t('listing_menu.actions.cannot_delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredMenus.length === 0 && !loading && !error && (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Plus className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isAdminOrAgency ? t('listing_menu.errors.no_menus_found') : t('listing_menu.errors.no_menus_yet')}
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {isAdminOrAgency 
                  ? t('listing_menu.errors.no_menus_system')
                  : t('listing_menu.errors.create_first_menu')}
              </p>
              {!isAdminOrAgency && (
                <Link
                  to="/menus/create"
                  className="inline-flex items-center gap-2 bg-[#0A0C0B] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1A1C1B] transition-colors"
                >
                  <Plus className="h-5 w-5" />
                  {t('listing_menu.buttons.create_new_menu')}
                </Link>
              )}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && filteredMenus.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4">
              <div className="text-sm text-[#687d76]">
                {t('listing_menu.pagination.showing', { 
                  start: (pagination.currentPage - 1) * pagination.limit + 1,
                  end: Math.min(pagination.currentPage * pagination.limit, pagination.totalMenus),
                  total: pagination.totalMenus
                })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage || loading}
                  className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('listing_menu.pagination.previous')}
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          pagination.currentPage === pageNum
                            ? 'bg-[#0A0C0B] text-white'
                            : 'text-[#687d76] hover:bg-gray-100'
                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={loading}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage || loading}
                  className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  {t('listing_menu.pagination.next')}
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MenuListPage;